import type { Ref } from 'vue'
import { onMounted } from 'vue'

export interface ScrollAnimationTargets {
  heroEl: Ref<HTMLElement | null>
  heroImgEl: Ref<HTMLElement | null>
  heroContentEl: Ref<HTMLElement | null>
  saleBannerEl: Ref<HTMLElement | null>
  saleBannerImgEl: Ref<HTMLElement | null>
  saleBannerContentEl?: Ref<HTMLElement | null>
}

type GsapContext = {
  revert: () => void
  add?: (fn: () => void) => void
}

type ScrollTriggerApi = {
  getAll: () => Array<{ kill: () => void }>
}

type GsapApi = {
  registerPlugin: (plugin: unknown) => void
  context: (fn: () => void, scope?: Element | Document | null) => GsapContext
  fromTo: (targets: HTMLElement | HTMLElement[], fromVars: Record<string, unknown>, toVars: Record<string, unknown>) => unknown
  to: (targets: HTMLElement | HTMLElement[], vars: Record<string, unknown>) => unknown
}

const REVEAL = { duration: 0.7, ease: 'power2.out' } as const
const SCROLL_TRIGGER = { start: 'top 85%', toggleActions: 'play none none none' } as const

function hasReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

function childElements(el: HTMLElement | null): HTMLElement[] {
  if (!el) return []
  return Array.from(el.children).filter((c): c is HTMLElement => c instanceof HTMLElement)
}

function elements(selector: string): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(selector))
}

function firstElement(selector: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(selector)
}

function preHide(el: HTMLElement, y = 30, x = 0): void {
  el.style.opacity = '0'
  if (y) el.style.transform = `translateY(${y}px)`
  else if (x) el.style.transform = `translateX(${x}px)`
}

export function useScrollAnimations(targets: ScrollAnimationTargets) {
  let gsap: GsapApi | null = null
  let context: GsapContext | null = null
  let scrollTriggerApi: ScrollTriggerApi | null = null
  let dynamicObserver: MutationObserver | null = null
  const animatedCarousels = new WeakSet<HTMLElement>()
  let recentlyAddedAnimated = false
  let editorialAnimated = false
  let initialized = false
  let disposed = false

  function runInContext(fn: () => void): void {
    if (context?.add) context.add(fn)
    else fn()
  }

  function reveal(
    g: GsapApi,
    els: HTMLElement[],
    fromVars: Record<string, unknown>,
    trigger: HTMLElement,
    extra: Record<string, unknown> = {},
  ): void {
    if (!els.length) return
    g.fromTo(els, fromVars, {
      ...REVEAL,
      opacity: 1,
      y: 0,
      x: 0,
      scale: 1,
      ...extra,
      scrollTrigger: { trigger, ...SCROLL_TRIGGER },
    })
  }

  function animateImgScale(
    g: GsapApi,
    containers: HTMLElement[],
    trigger: HTMLElement,
    stagger = 0.05,
  ): void {
    const imgs = containers.flatMap(el =>
      Array.from(el.querySelectorAll<HTMLElement>('.product-card__img:not(.product-card__img--alt)'))
    )
    if (!imgs.length) return
    g.fromTo(imgs, { scale: 1.08 }, {
      scale: 1,
      duration: 0.8,
      ease: 'power2.out',
      stagger,
      clearProps: 'scale',
      scrollTrigger: { trigger, ...SCROLL_TRIGGER },
    })
  }

  function animateCarousel(carousel: HTMLElement): void {
    if (animatedCarousels.has(carousel) || !gsap) return
    animatedCarousels.add(carousel)
    const slides = Array.from(carousel.querySelectorAll<HTMLElement>('.product-carousel__slide'))
    const section = carousel.closest<HTMLElement>('.products-section') ?? carousel
    if (slides.length) {
      slides.forEach(s => preHide(s, 25))
      runInContext(() => {
        reveal(gsap!, slides, { opacity: 0, y: 25 }, section, { stagger: 0.05 })
        animateImgScale(gsap!, slides, section, 0.05)
      })
    }
    else {
      preHide(carousel, 40)
      runInContext(() => reveal(gsap!, [carousel], { opacity: 0, y: 40 }, section))
    }
  }

  function animateRecentlyAddedGrid(): void {
    if (recentlyAddedAnimated || !gsap) return
    const grid = firstElement('.recently-added-grid')
    if (!grid) return
    const cards = Array.from(grid.querySelectorAll<HTMLElement>('.product-card'))
    if (!cards.length) return
    cards.forEach(c => preHide(c))
    runInContext(() => {
      reveal(gsap!, cards, { opacity: 0, y: 30 }, grid, { stagger: 0.06 })
      animateImgScale(gsap!, cards, grid, 0.06)
    })
    recentlyAddedAnimated = true
  }

  function animateEditorial(): void {
    if (editorialAnimated || !gsap) return
    const title = firstElement('.product-editorial-section__title')
    const section = firstElement('.product-editorial-section')
    if (!title || !section) return

    const items = elements(
      '.product-editorial-section__item:not(.product-editorial-section__skeleton-card)',
    )

    preHide(title)
    items.forEach(el => preHide(el))
    runInContext(() => {
      reveal(gsap!, [title], { opacity: 0, y: 30 }, title)
      if (items.length) {
        reveal(gsap!, items, { opacity: 0, y: 30 }, section, { stagger: 0.1 })
        animateImgScale(gsap!, items, section, 0.1)
      }
    })
    editorialAnimated = true
  }

  function observeDynamicContent(): void {
    if (dynamicObserver || typeof MutationObserver === 'undefined') return
    elements('.products-section .product-scroll-carousel').forEach(c => animateCarousel(c))
    animateRecentlyAddedGrid()
    animateEditorial()
    dynamicObserver = new MutationObserver(() => {
      if (disposed) return
      elements('.products-section .product-scroll-carousel').forEach(c => animateCarousel(c))
      if (!recentlyAddedAnimated) animateRecentlyAddedGrid()
      if (!editorialAnimated) animateEditorial()
    })
    dynamicObserver.observe(document.body, { childList: true, subtree: true })
  }

  function preHideTargets(): void {
    ;['.style-card', '.newsletter__head', '.newsletter__form'].forEach(
      sel => elements(sel).forEach(el => preHide(el)),
    )
  }

  function cleanup(): void {
    disposed = true
    initialized = false
    recentlyAddedAnimated = false
    editorialAnimated = false
    dynamicObserver?.disconnect()
    dynamicObserver = null
    context?.revert()
    context = null
    scrollTriggerApi?.getAll().forEach(t => t.kill())
    gsap = null
  }

  async function init(): Promise<void> {
    if (initialized || disposed || typeof window === 'undefined' || hasReducedMotion()) return
    initialized = true

    const [{ gsap: g }, { ScrollTrigger }] = await Promise.all([
      import('gsap'),
      import('gsap/ScrollTrigger'),
    ])
    if (disposed) return

    gsap = g as GsapApi
    scrollTriggerApi = ScrollTrigger as ScrollTriggerApi
    gsap.registerPlugin(scrollTriggerApi)

    context = gsap.context(() => {
      const g = gsap!

      // Hero parallax (scrub – no opacity reveal)
      if (targets.heroEl.value && targets.heroImgEl.value) {
        g.to(targets.heroImgEl.value, {
          yPercent: 20,
          ease: 'none',
          scrollTrigger: { trigger: targets.heroEl.value, start: 'top top', end: 'bottom top', scrub: true },
        })
      }

      // Ken Burns — ciągła animacja idle na hero image
      const heroImg = targets.heroImgEl.value?.querySelector<HTMLElement>('img')
      if (heroImg) {
        g.to(heroImg, {
          scale: 1.05,
          x: -20,
          duration: 15,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          delay: 1.3,
        })
      }

      // Sale banner parallax
      if (targets.saleBannerEl.value && targets.saleBannerImgEl.value) {
        g.to(targets.saleBannerImgEl.value, {
          yPercent: 20,
          ease: 'none',
          scrollTrigger: { trigger: targets.saleBannerEl.value, start: 'top bottom', end: 'bottom top', scrub: true },
        })
      }


      // Products section headers – one ScrollTrigger per section
      elements('.products-section:not(.products-section--first) .section-header').forEach(header => {
        const section = header.closest<HTMLElement>('.products-section')
        if (section) reveal(g, [header], { opacity: 0, y: 30 }, section)
      })

      // Carousels + recently-added grid (both loaded async, handled via observer)
      observeDynamicContent()

      // Style cards
      const styleCards = elements('.style-card')
      const styleGrid = firstElement('.style-grid')
      if (styleCards.length && styleGrid) {
        reveal(g, styleCards, { opacity: 0, y: 30, scale: 0.97 }, styleGrid, { stagger: 0.08 })
      }

      // Newsletter
      const newsletterEls = elements('.newsletter__head, .newsletter__form')
      const newsletter = firstElement('.newsletter')
      if (newsletterEls.length && newsletter) {
        reveal(g, newsletterEls, { opacity: 0, y: 30 }, newsletter, { stagger: 0.1 })
      }
    }, document.body)
  }

  onMounted(() => {
    disposed = false
    if (typeof window !== 'undefined' && !hasReducedMotion()) preHideTargets()
    void init()
  })

  return { cleanup }
}
